/**
 * Seletor de provedor de pagamento do Repasses (SRV-137).
 *
 * Durante a migração Zoop → Asaas a mesma marca tem carteira nos dois
 * provedores. Quando `settings.providers` traz mais de um, a ops escolhe qual
 * carteira está operando e TODA chamada de Repasses passa a levar o header
 * `x-payment-provider`. Marca com um provedor só não vê seletor e nada muda
 * pra ela (sem header → o pay-api usa o padrão da marca, como sempre).
 *
 * O seletor está liberado pro usuário da marca: quem tem 2+ provedores vê e
 * opera as duas carteiras, interno ou não. Até a Americanas ter a subconta
 * Asaas aprovada o seletor era ferramenta de teste interna (gate em
 * `PROVIDER_SELECTOR_INTERNAL_ONLY`, hoje `false`) — quem gatilha o que
 * aparece é só a contagem de `providers`.
 *
 * Lógica pura (sem React) pra ser testável: quem decide "mostra seletor?" e
 * "qual o valor inicial?" mora aqui; a persistência por seller group também.
 */
export type PaymentProvider = 'zoop' | 'asaas' | 'celcoin';

export const PAYMENT_PROVIDER_HEADER = 'x-payment-provider';

/**
 * Gate do seletor por tipo de usuário — hoje `false`: a marca vê o seletor e
 * manda `x-payment-provider` igual ao staff Abbiamo.
 *
 * Nasceu `true` na SRV-137, enquanto a subconta Asaas da Americanas não estava
 * aprovada: o seletor era ferramenta de teste interna e usuário da marca via o
 * Repasses como antes. Com o KYC aprovado, a marca precisa operar a carteira
 * Asaas, então o gate caiu. Segue sendo o ÚNICO ponto que decide isso (tudo
 * passa por `hasProviderSelector`), então voltar a travar é virar este
 * booleano.
 *
 * É um PAR com `PROVIDER_HEADER_INTERNAL_ONLY` no dashboard-api, e os dois
 * precisam andar juntos: só este em `false` mostra à marca um seletor sem
 * efeito (o BFF descarta o header de quem não é interno); só o de lá em
 * `false` libera um header que o front nunca manda.
 */
export const PROVIDER_SELECTOR_INTERNAL_ONLY = false;

const KNOWN_PROVIDERS: readonly PaymentProvider[] = [
  'zoop',
  'asaas',
  'celcoin',
];

export const isPaymentProvider = (value: unknown): value is PaymentProvider =>
  typeof value === 'string' &&
  (KNOWN_PROVIDERS as readonly string[]).includes(value);

/** "zoop" → "Zoop", "asaas" → "Asaas", "celcoin" → "Celcoin". */
export const providerLabel = (provider: string): string =>
  provider
    ? provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase()
    : '';

const PROVIDER_STATUS_LABELS: Record<string, string> = {
  active: 'Ativo',
  enabled: 'Ativo',
  approved: 'Aprovado',
  pending: 'Pendente',
  pending_review: 'Em análise',
  awaiting_documents: 'Aguardando documentos',
  denied: 'Recusado',
  rejected: 'Recusado',
  disabled: 'Desativado',
  unknown: 'Desconhecido',
};

/**
 * Status da conta do entregador no provedor, como o Pay grava (inglês, vindo
 * da Zoop/Asaas), traduzido pra tela. Status desconhecido sai como veio.
 */
export const providerStatusLabel = (
  status: string | null | undefined,
): string => {
  if (!status) return '';
  return PROVIDER_STATUS_LABELS[status.toLowerCase()] ?? status;
};

/** Só o que o seletor precisa do `GET /settings` (o resto é irrelevante). */
export type ProviderSettings =
  | {
      payment_provider?: string | null;
      providers?: string[] | null;
    }
  | null
  | undefined;

/**
 * Provedores disponíveis pra marca, já filtrados pro que o front conhece e
 * sem repetição. `providers` ausente (Pay antigo, antes da SRV-133) → [].
 */
export const availableProviders = (
  settings: ProviderSettings,
): PaymentProvider[] => {
  const raw: unknown[] = Array.isArray(settings?.providers)
    ? (settings?.providers ?? [])
    : [];
  const out: PaymentProvider[] = [];
  for (const p of raw) {
    if (isPaymentProvider(p) && !out.includes(p)) out.push(p);
  }
  return out;
};

/**
 * Seletor só existe com 2+ provedores — com 0/1 ninguém vê nada, interno ou
 * não. `internalOnly` é o gate de `PROVIDER_SELECTOR_INTERNAL_ONLY` (hoje
 * `false`, então `internal` não restringe nada); segue parametrizado pra ser
 * testável e pra travar de volta num só lugar.
 */
export const hasProviderSelector = (
  settings: ProviderSettings,
  internal: boolean,
  internalOnly: boolean = PROVIDER_SELECTOR_INTERNAL_ONLY,
): boolean =>
  (!internalOnly || internal) && availableProviders(settings).length > 1;

/**
 * Provedor efetivo do seletor. `null` = sem seletor = sem header.
 *
 * Com seletor, a prioridade é: o que a ops escolheu antes (localStorage) se
 * ainda estiver disponível → o padrão da marca (`payment_provider`) se estiver
 * em `providers` → o primeiro da lista.
 */
export const resolveProvider = (
  settings: ProviderSettings,
  stored: string | null | undefined,
  internal: boolean,
  internalOnly: boolean = PROVIDER_SELECTOR_INTERNAL_ONLY,
): PaymentProvider | null => {
  if (!hasProviderSelector(settings, internal, internalOnly)) return null;
  const available = availableProviders(settings);
  if (isPaymentProvider(stored) && available.includes(stored)) return stored;
  const brandDefault = settings?.payment_provider;
  if (isPaymentProvider(brandDefault) && available.includes(brandDefault)) {
    return brandDefault;
  }
  return available[0];
};

// ── Persistência por seller group ─────────────────────────────────
export const providerStorageKey = (sellerGroupId: string): string =>
  `repasses.provider.${sellerGroupId}`;

export const readStoredProvider = (
  sellerGroupId: string | null | undefined,
): PaymentProvider | null => {
  if (!sellerGroupId) return null;
  try {
    const value = window.localStorage.getItem(
      providerStorageKey(sellerGroupId),
    );
    return isPaymentProvider(value) ? value : null;
  } catch {
    return null;
  }
};

export const writeStoredProvider = (
  sellerGroupId: string | null | undefined,
  provider: PaymentProvider,
): void => {
  if (!sellerGroupId) return;
  try {
    window.localStorage.setItem(providerStorageKey(sellerGroupId), provider);
  } catch {
    /* localStorage indisponível (modo privado/quota) — o seletor segue em memória */
  }
};
